import { ChevronsUpDown, X } from 'lucide-react';
import { forwardRef } from 'react';

import { Button } from '@/shared/components/ui/Button/Button';
import { fieldVariants } from '@/shared/components/ui/field/field.tokens';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/Popover/Popover';
import { cn } from '@/shared/utils/cn/cn.util';

import { SELECTION_POPPER_CLASSES } from '../selection.tokens';

import { type SelectionTriggerProps } from './SelectionTrigger.types';

/**
 * The button that opens a selection panel, and the clear control beside it.
 *
 * The fourth drift surface, closed. `useSelectionValue` gave the family one
 * algebra, `useMultiSelectionPanel` one assembly and `SelectionPanel` one panel —
 * but the trigger shell was still written out three times (the popover, the
 * `asChild` button and its eight ARIA attributes, the truncating span, the
 * chevron, the clear `Button`), and it had already drifted: two of the three
 * reached for `multiSelect.clear` and the third for `serverSelect.clear`, two
 * catalogue keys for one affordance.
 *
 * The ref goes on the CHILD, never on `PopoverTrigger`: Radix's `Slot` composes
 * its own ref with the child's, and putting it on the trigger silently drops one
 * of the two — which is how react-hook-form's focus-on-error stops working with
 * no error anywhere.
 */
export const SelectionTrigger = forwardRef<HTMLButtonElement, SelectionTriggerProps>(
  function SelectionTrigger(
    {
      role,
      id,
      panelId,
      isOpen,
      size,
      isDisabled,
      isInvalid,
      placeholder,
      summary,
      onBlur,
      'aria-describedby': describedBy,
      'aria-labelledby': labelledBy,
      clear,
      children,
      onOpenChange,
    },
    ref,
  ) {
    return (
      <div className="flex items-center gap-sm">
        <Popover open={isOpen} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              id={id}
              type="button"
              role={role}
              aria-expanded={isOpen}
              aria-controls={panelId}
              aria-haspopup="dialog"
              aria-describedby={describedBy}
              aria-labelledby={labelledBy}
              disabled={isDisabled}
              onBlur={onBlur}
              // `aria-invalid` is not supported on a button (it has no value to
              // be invalid), so the state is carried by the `isInvalid` variant
              // and the message is rendered by the field around it.
              className={cn(fieldVariants({ fieldSize: size, shape: 'trigger', isInvalid }))}
            >
              <span className={cn('truncate', summary === '' && 'text-muted-foreground')}>
                {summary === '' ? placeholder : summary}
              </span>
              <ChevronsUpDown aria-hidden className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>

          <PopoverContent id={panelId} className={SELECTION_POPPER_CLASSES}>
            {children}
          </PopoverContent>
        </Popover>

        {clear === undefined ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={clear.label}
            onClick={clear.onClear}
          >
            <X aria-hidden className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  },
);
